import { get as getConfig } from 'config'
import { get as apiGet, TYPE_BROOKLYN } from 'api-client'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _keys from 'lodash/keys'
import _parseInt from 'lodash/parseInt'
import _reduce from 'lodash/reduce'
import _replace from 'lodash/replace'
import _size from 'lodash/size'
import {
  getContentType,
  TYPE_CMS_NODE,
  TYPE_CONTENT_VIDEO_MEDITATION,
} from 'services/content-type'
import { OG_TYPE_ARTICLE } from 'services/open-graph'
import { format as formatDateTime } from 'services/date-time'
import { getGeoAvailabilityCode } from 'services/geo-restrictions'
import {
  SUBSCRIPTION_OFFERINGS_AVAILABILITY_IS_FREE,
  SUBSCRIPTION_OFFERINGS_AVAILABILITY_ONLY_WITH,
} from 'services/subscription'
import {
  setCookie,
  getCookie,
  expireCookie,
} from 'services/cookie'
import { SCHEMA, ASSETS_VIDEO_TILE_SCHEMA } from './schema'

const ARTICLE_EMAIL_CAPTURE_COOKIE_NAME = 'articleEmailCapture'
export const ARTICLE_EMAIL_CAPTURE_COOKIE_VALUE = 1

export function getEmailCapturePersistent () {
  return getCookie(ARTICLE_EMAIL_CAPTURE_COOKIE_NAME)
}

export function expireEmailCapturePersistent () {
  expireCookie(ARTICLE_EMAIL_CAPTURE_COOKIE_NAME)
}

export function writeEmailCapturePersitent (value, expires, uid, authToken) {
  setCookie(ARTICLE_EMAIL_CAPTURE_COOKIE_NAME, value, '/', expires, false, uid, authToken)
}

export async function get (options = {}) {
  const { id, locale, language } = options
  try {
    const res = await apiGet(`node/${id}`, { f: 'md', language }, null, TYPE_BROOKLYN)
    return handleGetArticleResponse(id, res, locale)
  } catch (e) {
    return handleGetArticleResponse(id, {}, locale, true)
  }
}

export function handleGetArticleResponse (id, res, locale, _dataError) {
  const data = _assign({ id }, _get(res, 'body', {}))
  return createModel(data, locale, _dataError)
}

export function createModel (data, locale, _dataError) {
  const createdTimestamp = _parseInt(_get(data, 'created', -1))
  const image = _get(data, 'article_main_image.article_820x461')
  const { origin } = getConfig()
  return _assign(_cloneDeep(SCHEMA), {
    id: _get(data, 'id'),
    assets: formatAssets(_get(data, 'fields.body[0].assets')),
    format: _get(data, 'fields.body[0].format'),
    body: _get(data, 'fields.body[0].value'),
    title: _get(data, 'title'),
    author: _get(data, 'article_author'),
    createdTimestamp,
    dateTimeText: formatDateTime(createdTimestamp * 1000, locale),
    mainImage: _get(data, 'article_main_image.article_820x461'),
    commentCount: _get(data, 'comment_count'),
    siteSegment: {
      id: _parseInt(_get(data, 'site_segment.id', -1)),
      name: _get(data, 'site_segment.name'),
    },
    seo: {
      title: _get(data, 'seo.title')
        ? _get(data, 'seo.title')
        : _get(data, 'title'),
      description: _get(data, 'seo.description', null),
      robots: {
        noIndex: _get(data, 'seo.robots.noIndex', false),
        noFollow: _get(data, 'seo.robots.noFollow', false),
      },
      schema: {
        title: _get(data, 'title'),
        datePublished: new Date(createdTimestamp * 1000).toISOString(),
        image: image ? [image] : null,
      },
    },
    social: {
      og: {
        title: _get(data, 'title', ''),
        image,
        description: _replace(
          _get(data, ['fields', 'teaser', 0, 'value'], ''),
          /\r?\n|\r/,
          '',
        ),
        type: OG_TYPE_ARTICLE,
        url: `${origin}/${_get(data, 'path', '')}`,
      },
    },
    vote: _get(data, 'fivestar.up_count.value', 0),
    voteDown: _get(data, 'fivestar.down_count.value', 0),
    _dataError,
  })
}

function formatAssets (assets) {
  return _reduce(
    assets,
    (acc, elem, key) => {
      if (elem && elem.type && elem.type === 'video') {
        const data = elem.data || {}
        const contentType = getContentType(TYPE_CMS_NODE, elem.data)
        // eslint-disable-next-line no-param-reassign
        elem.data = _assign(_cloneDeep(ASSETS_VIDEO_TILE_SCHEMA), {
          type: _get(data, 'type', null),
          id: _parseInt(_get(data, 'nid', -1)),
          title: _get(data, 'title', null),
          heroImage: formatVideoTileHeroImage(
            _get(data, 'hero_image_notext', {}),
          ),
          duration: _parseInt(_get(data, 'feature.duration', 0)),
          vote: _parseInt(_get(data, 'fivestar.up_count.value', 0)),
          voteDown: _parseInt(_get(data, 'fivestar.down_count.value', 0)),
          url: _get(data, 'path', null),
          episode: _parseInt(_get(data, 'fields.episode[0].value', 0)),
          season: _parseInt(_get(data, 'fields.season[0].value', 0)),
          seriesTitle: _get(data, 'series.title', null),
          seriesPath: _get(data, 'series.path', null),
          host: _get(data, 'fields.instructor[0].value', null),
          guest: _get(data, 'fields.cast[0].value', null),
          year: _get(data, 'fields.copyright[0].value', null),
          yogaStyle: _get(data, 'classicFacets.yogaStyle[0].name', null),
          yogaLevel: _get(data, 'classicFacets.yogaLevel[0].name', null),
          fitnessStyle: _get(data, 'classicFacets.fitnessStyle[0].name', null),
          fitnessLevel: _get(data, 'classicFacets.fitnessLevel[0].name', null),
          meditationStyle:
            contentType === TYPE_CONTENT_VIDEO_MEDITATION ? 'Meditation' : null,
          feature: formatVideoTileData(_get(data, 'feature', {})),
          preview: formatVideoTileData(_get(data, 'preview', {})),
        })
      }
      acc[key] = elem
      return acc
    },
    {},
  )
}

function formatVideoTileHeroImage (data) {
  return {
    small: _get(data, 'hero_320x200', null),
    mediumSmall: _get(data, 'hero_570x300', null),
    medium: _get(data, 'hero_820x400', null),
    large: _get(data, 'hero_1070x400', null),
  }
}

function formatVideoTileData (data) {
  const isFree = _get(data, 'isFree', false)
  const subscriptions = _get(data, 'offerings', [])
  const availability =
    isFree || _size(subscriptions) === 0
      ? SUBSCRIPTION_OFFERINGS_AVAILABILITY_IS_FREE
      : SUBSCRIPTION_OFFERINGS_AVAILABILITY_ONLY_WITH

  return {
    id: _parseInt(_get(data, 'nid', -1)),
    offerings: {
      availability,
      subscriptions,
    },
    georestrictions: {
      availability: getGeoAvailabilityCode(
        _parseInt(_get(data, 'georestrictions.availability', -1)),
      ),
      countries: _keys(_get(data, 'georestrictions.countries', {})),
    },
    duration: _parseInt(_get(data, 'duration', 0)),
  }
}
